import { useEffect, useState } from 'react'

import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import InfluencerDetails from '@src/quince-site-common/src/domains/InfluencerDetails'
import Product from '@src/quince-site-common/src/domains/Product'
import productService from '@src/quince-site-common/src/services/productService'
import { captureException, captureFatalException } from '@src/quince-site-common/src/utils/sentryUtil'

import { InfluencerCardProductDetails } from '../atoms/influencerCard/InfluencerCard'
import { getInitialSelectedOptions, isNestedProduct } from '../molecules/productCard/productCard.helpers'

const FATAL_EXCEPTION_MESSAGE = 'Failed to fetch products in InfluencerSection section'

// Telemetry tags for the id-lookup failure breadcrumb (module-level consts, not user copy).
const ID_SPACE_INTERNAL_PRODUCT_IDS = 'internalProductIds'
const ID_SPACE_SPIDS = 'spids'

function buildInfluencerCardProductDetails(product: Product): InfluencerCardProductDetails {
  const selectedOptions = getInitialSelectedOptions(product, [], true, false, isNestedProduct(product) ? 2 : 1)

  return {
    product,
    selectedOptions,
    productTags: product.tags,
  }
}

function getUniqueContentfulProductIds(influencerDetailList: InfluencerDetails[]) {
  return [
    ...new Set(
      influencerDetailList
        .map(influencerDetail => influencerDetail.contentfulProductId)
        .filter((id): id is string => id != null && id !== ''),
    ),
  ]
}

async function fetchInfluencerSectionProducts(
  influencerDetailList: InfluencerDetails[],
  isB2BSelfService?: boolean,
): Promise<Product[]> {
  const spids = !isB2BSelfService
    ? influencerDetailList.map(influencerDetail => influencerDetail.spid).filter(id => id != null)
    : undefined
  const contentfulProductIds = isB2BSelfService ? getUniqueContentfulProductIds(influencerDetailList) : undefined

  if (isB2BSelfService) {
    return (
      (await productService.getProducts({
        contentfulProductIds,
        useLite: true,
      })) ?? []
    )
  }

  // PL cards carry the INTERNAL product id (the adapter surfaces it as `internalProductId`);
  // sending it as a spid makes /products/enrich 404 on every load and forces the contentful
  // fallback below — so resolve by internal id when present. Non-PL (contentful-sourced) details
  // never set it and keep the spid lookup.
  const internalProductIds = [
    ...new Set(
      influencerDetailList
        .map(influencerDetail => influencerDetail.internalProductId)
        .filter((id): id is number => id != null),
    ),
  ]

  // The id-lookup failures are swallowed on purpose (the contentful fallback below still renders
  // the cards), but narrated to Sentry — an invisible fallback is how the guaranteed-404 spid
  // lookup went unnoticed in the first place.
  const captureIdLookupFailure = (error: unknown, idSpace: string, idCount: number) => {
    captureException(error, {
      module: MODULES.PRODUCT,
      feature: FEATURES.GET_PRODUCT,
      extraInfo: { capturedFrom: 'fetchInfluencerSectionProducts', idSpace, idCount },
    })
  }

  let products: Product[] = []
  if (internalProductIds.length) {
    try {
      products = (await productService.getProducts({ internalProductIds, useLite: true })) ?? []
    } catch (error) {
      captureIdLookupFailure(error, ID_SPACE_INTERNAL_PRODUCT_IDS, internalProductIds.length)
      products = []
    }
  } else if (spids?.length) {
    try {
      products = (await productService.getProducts({ spids, useLite: true })) ?? []
    } catch (error) {
      captureIdLookupFailure(error, ID_SPACE_SPIDS, spids.length)
      products = []
    }
  }

  // Contentful fallback fills only the cards the id lookup did NOT resolve — a partial id
  // response must not skip it, or cards that used to resolve via contentfulProductId would
  // silently render non-shoppable. A fallback failure keeps any partial results (rethrown only
  // when nothing resolved, preserving the original all-or-nothing error path).
  const resolvedIds = collectResolvedProductIds(products)
  const fallbackContentfulProductIds = getUniqueContentfulProductIds(
    influencerDetailList.filter(influencerDetail => !isDetailResolved(influencerDetail, resolvedIds)),
  )
  if (fallbackContentfulProductIds.length) {
    try {
      const fallbackProducts =
        (await productService.getProducts({
          contentfulProductIds: fallbackContentfulProductIds,
          useLite: true,
        })) ?? []
      products = [...products, ...fallbackProducts]
    } catch (error) {
      if (!products.length) throw error
    }
  }

  return products
}

interface ResolvedProductIds {
  internalIds: Set<number>
  spids: Set<number>
  contentfulIds: Set<string>
}

function collectResolvedProductIds(products: Product[]): ResolvedProductIds {
  // `product.id` / `product.spid` are typed `number | string` — normalize to number so
  // `Set.has` agrees with the strictly-numeric `InfluencerDetails` ids (bracket lookups in the
  // effect's map-builder coerce implicitly; Sets don't).
  return {
    internalIds: new Set(products.map(product => Number(product.id))),
    spids: new Set(products.map(product => Number(product.spid))),
    contentfulIds: new Set(products.map(product => product.contentfulProductId)),
  }
}

/**
 * Whether the fetched products cover this card — same id precedence the product-map builder in
 * the hook effect uses: internal id (`product.id`, PL cards), then spid, then contentful id.
 */
function isDetailResolved(influencerDetail: InfluencerDetails, resolvedIds: ResolvedProductIds): boolean {
  return (
    (influencerDetail.internalProductId != null && resolvedIds.internalIds.has(influencerDetail.internalProductId)) ||
    (influencerDetail.spid != null && resolvedIds.spids.has(influencerDetail.spid)) ||
    (!!influencerDetail.contentfulProductId && resolvedIds.contentfulIds.has(influencerDetail.contentfulProductId))
  )
}

function useInfluencerCardProducts(influencerDetailList: InfluencerDetails[], isB2BSelfService?: boolean) {
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true)
  const [products, setProducts] = useState<Product[]>()
  const [spidToProductMap, setSpidToProductMap] = useState<Record<number, InfluencerCardProductDetails>>({})
  const [contentfulProductIdToProductMap, setContentfulProductIdToProductMap] = useState<
    Record<string, InfluencerCardProductDetails>
  >({})

  useEffect(() => {
    void (async () => {
      try {
        setIsLoadingProducts(true)
        const products = await fetchInfluencerSectionProducts(influencerDetailList, isB2BSelfService)

        if (isB2BSelfService) {
          const contentfulProductIdToProductMap: Record<string, InfluencerCardProductDetails> = {}
          products.forEach(product => {
            contentfulProductIdToProductMap[product.contentfulProductId] = buildInfluencerCardProductDetails(product)
          })
          setContentfulProductIdToProductMap(contentfulProductIdToProductMap)
        } else {
          const productBySpid = Object.fromEntries(products.map(product => [product.spid, product]))
          // `product.id` is the internal product id — pairs with `influencerDetail.internalProductId`
          // (PL cards), whose `spid` holds the same internal id and so never matches `product.spid`.
          const productByInternalId = Object.fromEntries(products.map(product => [product.id, product]))
          const productByContentfulId = Object.fromEntries(
            products.map(product => [product.contentfulProductId, product]),
          )
          const spidToProductMap: Record<number, InfluencerCardProductDetails> = {}

          influencerDetailList.forEach(influencerDetail => {
            const product =
              (influencerDetail.internalProductId != null && productByInternalId[influencerDetail.internalProductId]) ||
              (influencerDetail.spid != null && productBySpid[influencerDetail.spid]) ||
              (influencerDetail.contentfulProductId && productByContentfulId[influencerDetail.contentfulProductId])

            if (!product || influencerDetail.spid == null) return

            spidToProductMap[influencerDetail.spid] = buildInfluencerCardProductDetails(product)
          })

          setSpidToProductMap(spidToProductMap)
        }
        setProducts(products)
      } catch (error) {
        captureFatalException(FATAL_EXCEPTION_MESSAGE, {
          error: JSON.stringify(error),
        })
      } finally {
        setIsLoadingProducts(false)
      }
    })()
  }, [influencerDetailList, isB2BSelfService])

  return {
    isLoadingProducts,
    products,
    spidToProductMap,
    contentfulProductIdToProductMap,
  }
}

export { useInfluencerCardProducts }
